import usePageTitle from '../../hooks/usePageTitle';
import React, { useCallback, useState, useEffect } from 'react';
import {
  User,
  Mail,
  Building2,
  Shield,
  Calendar,
  Edit3,
  Save,
  X,
} from 'lucide-react';
import { User as ApiUser, Client } from '../../types';
import { usersService, clientsService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { formatLongDate } from '../../utils/dateUtils';
import './ClientPortalProfile.css';
import NotificationPrefsCard from '../../components/NotificationPrefsCard';

const ClientPortalProfile: React.FC = () => {
  usePageTitle('Profile');
  const { currentUser, setCurrentUser, loading: authLoading } = useAuth();
  const { showSuccess, showError } = useToast();
  const [user, setUser] = useState<ApiUser | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!currentUser) {
      setUser(null);
      setClient(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const resolvedUser = currentUser;
      setUser(resolvedUser);
      const clientData = currentUser.client_id
        ? await clientsService.getClientById(currentUser.client_id)
        : null;
      setClient(clientData);
      setEditedName(resolvedUser.name);
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);

  const handleEditToggle = () => {
    if (isEditing && user) {
      setEditedName(user.name);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!user) return;
    const trimmed = editedName.trim();
    if (!trimmed) {
      showError('Name cannot be empty');
      return;
    }
    if (trimmed === user.name) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await usersService.updateUser(user.id, { name: trimmed });
      setUser(updated);
      setEditedName(updated.name);
      setCurrentUser(updated);
      showSuccess('Profile updated');
      setIsEditing(false);
    } catch (err) {
      showError(
        'Could not update profile',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'client':
        return { label: 'Client Admin', class: 'role-client' };
      case 'facility_manager':
        return { label: 'Facility Manager', class: 'role-manager' };
      case 'consultant':
        return { label: 'Consultant', class: 'role-consultant' };
      default:
        return { label: role, class: 'role-default' };
    }
  };

  if (loading) {
    return (
      <div className="client-profile-loading">
        <div className="loading-spinner" />
        <p>Loading your profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="client-profile-error">
        <User size={48} />
        <h3>Profile Not Found</h3>
        <p>Unable to load your profile information.</p>
      </div>
    );
  }

  const roleBadge = getRoleBadge(user.role);

  return (
    <div className="client-portal-profile">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <User size={28} />
            My Profile
          </h1>
          <p>Manage your account information</p>
        </div>
      </div>

      <div className="profile-content">
        {/* Profile Card */}
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              <span>
                {user.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </span>
            </div>
            <div className="profile-info">
              {isEditing ? (
                <div className="edit-name">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="name-input"
                    autoFocus
                  />
                </div>
              ) : (
                <h2>{user.name}</h2>
              )}
              <span className={`role-badge ${roleBadge.class}`}>
                <Shield size={14} />
                {roleBadge.label}
              </span>
            </div>
            <div className="profile-actions">
              {isEditing ? (
                <>
                  <button
                    className="btn btn-ghost"
                    onClick={handleEditToggle}
                    disabled={saving}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    <Save size={16} />
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={handleEditToggle}>
                  <Edit3 size={16} />
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          <div className="profile-details">
            <div className="detail-row">
              <div className="detail-label">
                <Mail size={18} />
                Email
              </div>
              <div className="detail-value">{user.email}</div>
            </div>

            {client && (
              <div className="detail-row">
                <div className="detail-label">
                  <Building2 size={18} />
                  Organization
                </div>
                <div className="detail-value">{client.name}</div>
              </div>
            )}

            <div className="detail-row">
              <div className="detail-label">
                <Calendar size={18} />
                Member Since
              </div>
              <div className="detail-value">
                {formatLongDate(user.created_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="info-card">
          <h3>Account Information</h3>
          <p>
            Your account is managed through the H2Okay water management platform.
            Contact your administrator if you need to update your email or role.
          </p>
        </div>
        <div data-tour="portal-profile">
          <NotificationPrefsCard
            user={user}
            onSaved={(updated) => {
              setUser(updated);
              if (currentUser && updated.id === currentUser.id) {
                setCurrentUser(updated);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientPortalProfile;
